/**
 * Shared renderer for one ToolArtifact — used both inline beneath
 * assistant messages (where the user actually wants to see the
 * picture) and inside the "show your work" panel.
 *
 * URL handling:
 *   • `/v1/artifacts/<id>` (relative, server-served) — prepend the API
 *     base and load with credentials so the auth cookie travels. This
 *     is the path that decouples binary tool output from the LLM
 *     conversation history.
 *   • `data:image/...`, `http(s)://...` — pass through. Used for
 *     historical messages persisted before the artifact endpoint
 *     existed; the renderer must keep handling them so old chats
 *     don't lose their charts.
 *   • `file://`, `gs://` — never reachable from a browser. Fall back
 *     to plaintext so the user can at least see/copy the path.
 */
import { env } from "@/env";
import type { ToolArtifact } from "@models/chat";
import BrokenImageIcon from "@mui/icons-material/BrokenImageOutlined";
import DownloadIcon from "@mui/icons-material/FileDownloadOutlined";
import {
  Box,
  CircularProgress,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { ChartArtifact } from "./ChartArtifact";
import { SchematicSvg } from "./SchematicSvg";

const CHART_CONTENT_TYPE = "application/vnd.cf.chart+json";

/** Resolve an artifact URL to something a browser can fetch. */
function resolveUrl(url: string): { src: string } {
  // Server-relative path → prepend API base.
  if (url.startsWith("/")) {
    return { src: `${env.VITE_API_BASE_URL}${url}` };
  }
  // data:, http(s):, anything else absolute — use as-is.
  return { src: url };
}

/**
 * Trigger a browser download of `bytes` as a file named `filename`.
 * Pure DOM — duplicates the helper in MermaidDiagram intentionally so
 * neither component has to depend on the other.
 */
function _downloadBlob(bytes: Blob, filename: string) {
  const url = URL.createObjectURL(bytes);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Free the blob URL on the next tick — Chrome leaks if you revoke
  // immediately during a download click.
  setTimeout(() => URL.revokeObjectURL(url), 250);
}

/** Map a mime type to a sensible file extension for the download. */
function _extensionFor(mime: string | undefined, fallback = "png"): string {
  if (!mime) return fallback;
  const m = mime.toLowerCase();
  if (m.includes("png")) return "png";
  if (m.includes("jpeg") || m.includes("jpg")) return "jpg";
  if (m.includes("svg")) return "svg";
  if (m.includes("webp")) return "webp";
  if (m.includes("gif")) return "gif";
  if (m.includes("json")) return "json";
  if (m.includes("csv")) return "csv";
  if (m.includes("xml")) return "xml";
  if (m.includes("yaml") || m.includes("x-yaml")) return "yaml";
  if (m.includes("text/plain") || m === "text") return "txt";
  return fallback;
}

/** Slug-ify an artifact label into a safe filename stem. */
function _slugifyLabel(label: string | undefined, fallback: string): string {
  const slug = (label ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
  return slug || fallback;
}

/**
 * Build a download filename for an artifact. Prefers an extension
 * already present at the end of the label (so a SPICE netlist
 * labelled `…-netlist.cir` downloads as `.cir` rather than the
 * generic `.txt` we'd infer from `text/plain`). Falls back to the
 * MIME inference when the label has no recognizable extension.
 */
function _filenameForArtifact(
  label: string | undefined,
  mime: string | undefined,
  fallbackStem: string,
  fallbackExt: string,
): string {
  const labelStr = (label ?? "").trim();
  const extMatch = labelStr.match(/\.([a-z0-9]{1,6})$/i);
  if (extMatch) {
    const stem = _slugifyLabel(
      labelStr.slice(0, labelStr.length - extMatch[0].length),
      fallbackStem,
    );
    return `${stem}-${Date.now()}.${extMatch[1].toLowerCase()}`;
  }
  const ext = _extensionFor(mime, fallbackExt);
  const stem = _slugifyLabel(labelStr, fallbackStem);
  return `${stem}-${Date.now()}.${ext}`;
}

interface ArtifactViewProps {
  artifact: ToolArtifact;
  /** Layout variant. `inline` is used beneath assistant messages where
   *  the chart IS the answer; `panel` is the side-panel "show your
   *  work" rendering, which is denser. */
  variant?: "inline" | "panel";
  /** Pass-through to `ChartArtifact.hideSetupHeader` — only meaningful
   *  for chart artifacts. Lets a host surface (e.g. the Workbench
   *  simulation tab) suppress the chart's redundant "what was
   *  simulated" caption when the surrounding layout already shows
   *  that data. */
  hideChartSetupHeader?: boolean;
}

/**
 * Fetch + render an SVG artifact via SchematicSvg so the dark-theme
 * recolor pass actually runs. The default `<img>` path renders the
 * artifact as an opaque raster blob — schemdraw's hard-coded black
 * strokes stay black, the white card sits on a navy chat surface, and
 * the user gets the "bright island" treatment we just spent a commit
 * fixing. Fetching the SVG inline routes it through SchematicSvg's
 * regex recolor instead.
 *
 * Why a separate fetch path: the artifact endpoint returns SVG bytes
 * (or 302s to a GCS signed URL); both work with `credentials:
 * "include"` for the local stream and a follow-up no-cred fetch for
 * the signed URL. We need the SVG as a string to inject into the
 * recolor pass — `<img>` would render the SVG but in a closed shadow
 * tree we can't restyle.
 */
function SvgArtifactBody({ src }: { src: string }) {
  const [svg, setSvg] = useState<string | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    // AbortController so a quickly-changing src (e.g. user scrolling through
    // diff snapshots) cancels the in-flight request instead of just discarding
    // its bytes — keeps memory + connection use bounded.
    const ac = new AbortController();
    (async () => {
      try {
        const resp = await fetch(src, {
          credentials: "include",
          signal: ac.signal,
        });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const text = await resp.text();
        setSvg(text);
      } catch (err) {
        if ((err as { name?: string })?.name === "AbortError") return;
        setError(true);
      }
    })();
    return () => ac.abort();
  }, [src]);

  if (error) {
    return (
      <Stack direction="row" spacing={1} alignItems="center">
        <BrokenImageIcon sx={{ fontSize: 18, color: "text.secondary" }} />
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          Schematic unavailable
        </Typography>
      </Stack>
    );
  }
  if (svg === null) {
    return (
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ py: 2, px: 1.5, color: "text.secondary" }}
      >
        <CircularProgress size={14} />
        <Typography variant="caption">Loading schematic…</Typography>
      </Stack>
    );
  }
  return <SchematicSvg svg={svg} />;
}

export function ArtifactView({
  artifact,
  variant = "panel",
  hideChartSetupHeader = false,
}: ArtifactViewProps) {
  const [loadError, setLoadError] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const ct = (artifact.content_type ?? "").toLowerCase();
  const isSvg = ct.startsWith("image/svg");
  const isChart = artifact.kind === "chart" || ct === CHART_CONTENT_TYPE;
  const isImage =
    !isSvg &&
    !isChart &&
    (artifact.kind === "image" || ct.startsWith("image/"));
  const { src } = resolveUrl(artifact.url);
  // Server-relative paths and GCS signed URLs (https://) are
  // browser-fetchable; file:// and gs:// schemes are not. The
  // download button only makes sense when we can actually fetch.
  const isFetchable =
    artifact.url.startsWith("/") ||
    /^https?:/i.test(artifact.url) ||
    artifact.url.startsWith("data:");

  /**
   * Trigger a browser download for this artifact.
   *
   * The naive `fetch()` → blob → `URL.createObjectURL` path is what
   * we used for images, and what broke for the netlist / JSON
   * artifacts: in production the artifact endpoint 302-redirects to
   * a GCS signed URL, and `fetch()` with `credentials: "include"`
   * fails the cross-origin preflight against GCS — the error got
   * swallowed and the button silently did nothing.
   *
   * Instead, for server-relative URLs we route through
   * `/v1/artifacts/<id>?download=1`. The backend bakes
   * `Content-Disposition: attachment` (with a filename derived from
   * the artifact label) into the signed URL before redirecting, so
   * the browser follows the redirect natively — auth cookie travels,
   * no CORS preflight, and the file lands on disk with the right
   * name. Works identically against the local-FS streaming fallback.
   *
   * For absolute (`https://…`, `data:…`) URLs we still use the
   * fetch+blob path since those don't flow through our `?download`
   * flag.
   */
  const downloadArtifact = async (
    fallbackStem: string,
    fallbackExt: string,
  ) => {
    if (downloading || !isFetchable) return;
    const filename = _filenameForArtifact(
      artifact.label,
      artifact.content_type,
      fallbackStem,
      fallbackExt,
    );

    if (artifact.url.startsWith("/")) {
      // Server-relative — let the browser handle auth + redirect.
      const sep = src.includes("?") ? "&" : "?";
      const a = document.createElement("a");
      a.href = `${src}${sep}download=1`;
      a.download = filename;
      a.rel = "noopener";
      // Some browsers (Firefox) require the anchor to be in the DOM
      // for the synthetic click to fire. Append → click → remove.
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      return;
    }

    // Absolute URL — fetch+blob path. Used for legacy `data:` /
    // external `https://` artifacts that don't go through our
    // backend's `?download` flag.
    setDownloading(true);
    try {
      const response = await fetch(src, { credentials: "omit" });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      _downloadBlob(blob, filename);
    } catch (err) {
      console.warn("Artifact download failed:", err);
    } finally {
      setDownloading(false);
    }
  };

  // Interactive chart spec (cf-chart/v1). Routed before any image
  // branch so a malformed `content_type` can't accidentally fall
  // through into the <img> path.
  if (isChart) {
    return (
      <ChartArtifact
        artifact={artifact}
        variant={variant}
        hideSetupHeader={hideChartSetupHeader}
      />
    );
  }

  // SVG schematics get a dedicated path: fetch the bytes, hand them to
  // SchematicSvg which recolours schemdraw's black strokes for the dark
  // theme. The generic <img> branch below would render the SVG as an
  // opaque image, blocking the recolour and leaving the schematic
  // black-on-white inside a navy chat — the bug the user just hit.
  if (isSvg) {
    return (
      <Box sx={{ ...(variant === "inline" && { my: 1.25 }) }}>
        {artifact.label && (
          <Typography
            variant="caption"
            sx={{
              display: "block",
              color: "text.secondary",
              fontSize: "0.7rem",
              mb: 0.5,
            }}
          >
            {artifact.label}
          </Typography>
        )}
        <SvgArtifactBody src={src} />
      </Box>
    );
  }

  if (isImage) {
    // Failed to fetch — render a small "unavailable" notice instead of
    // a silent broken-image icon. Common causes: artifact swept by a
    // future cleanup job, storage backend hiccup, ownership mismatch
    // returning 404. The user at least learns SOMETHING was supposed
    // to be here, plus the label tells them what.
    if (loadError) {
      return (
        <Box
          sx={{
            border: "1px dashed",
            borderColor: "border.default",
            borderRadius: 1,
            p: 1.25,
            ...(variant === "inline" && { my: 1.25 }),
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <BrokenImageIcon sx={{ fontSize: 18, color: "text.secondary" }} />
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", fontSize: "0.75rem" }}
            >
              {artifact.label
                ? `"${artifact.label}" — chart unavailable`
                : "Chart unavailable"}
            </Typography>
          </Stack>
        </Box>
      );
    }

    const handleDownload = () => downloadArtifact("circuit-forge-image", "png");

    return (
      <Box
        sx={{
          ...(variant === "inline" && { my: 1.25 }),
          // Lift the download button to full opacity when the user is
          // actually engaging with the image.
          "&:hover .artifact-toolbar, &:focus-within .artifact-toolbar": {
            opacity: 1,
          },
        }}
      >
        {artifact.label && (
          <Typography
            variant="caption"
            sx={{
              display: "block",
              color: "text.secondary",
              fontSize: "0.7rem",
              mb: 0.5,
            }}
          >
            {artifact.label}
          </Typography>
        )}
        {/* Image + floating button share an inline-block positioning
            context so the button hugs the image's right edge regardless
            of the chat column width. The chart PNGs already carry their
            own dark navy figure background (see viz_tools._apply_…), so
            no card chrome is needed — adding one just creates a visible
            seam against the chat surface. */}
        <Box
          sx={{
            position: "relative",
            display: "inline-block",
            maxWidth: "100%",
            lineHeight: 0,
          }}
        >
          <Box
            component="img"
            src={src}
            alt={artifact.label || "Tool artifact"}
            // Deliberately NO `crossOrigin` attribute. The artifact
            // endpoint may 302 to a GCS signed URL — and GCS does not
            // return `Access-Control-Allow-Credentials`, so a CORS
            // request with `crossOrigin="use-credentials"` would block
            // the redirected response. Browsers send our `SameSite=None;
            // Secure` session cookie on no-cors `<img>` loads anyway,
            // so the bytes-streaming fallback path (when the backend
            // can't issue a signed URL) keeps working without it.
            onError={() => setLoadError(true)}
            sx={{
              maxWidth: "100%",
              height: "auto",
              display: "block",
              borderRadius: 1,
            }}
          />

          {/* Floating download button. Visible at low opacity by default
              so it's discoverable on touch / keyboard users without
              requiring a hover; full opacity once the user engages. */}
          <Tooltip
            title={downloading ? "Downloading…" : "Download image"}
            arrow
            placement="left"
          >
            <Box
              component="button"
              type="button"
              onClick={handleDownload}
              disabled={downloading}
              aria-label="Download image"
              className="artifact-toolbar"
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 30,
                height: 30,
                padding: 0,
                borderRadius: 1,
                border: "1px solid",
                borderColor: "border.default",
                bgcolor: "rgba(10, 16, 34, 0.82)",
                color: "rgba(230, 237, 247, 0.85)",
                cursor: downloading ? "wait" : "pointer",
                opacity: 0.65,
                transition: "opacity 140ms, background 140ms, color 140ms",
                backdropFilter: "blur(4px)",
                "&:hover:not(:disabled)": {
                  bgcolor: "rgba(15, 23, 42, 0.92)",
                  color: "#e6edf7",
                },
                "&:disabled": { cursor: "wait" },
                "&:focus-visible": { opacity: 1 },
              }}
            >
              {downloading ? (
                <CircularProgress size={14} sx={{ color: "inherit" }} />
              ) : (
                <DownloadIcon sx={{ fontSize: 16 }} />
              )}
            </Box>
          </Tooltip>
        </Box>
      </Box>
    );
  }

  // Generic link artifact (SPICE netlist `.cir`, simulation result
  // JSON, future CSVs, etc.). Render the label + URL as before, plus
  // a download button when the URL is fetchable so the user can
  // actually get the bytes — for SPICE this is what makes the
  // generated model and raw simulation output downloadable.
  // file:// / gs:// schemes aren't browser-fetchable; they fall
  // through as plaintext with no button so the user can still copy
  // the path.
  const clickable = /^https?:/i.test(artifact.url);
  const handleDownload = () =>
    downloadArtifact("circuit-forge-artifact", "txt");

  return (
    <Box
      sx={{
        bgcolor: "surface.raised",
        border: "1px solid",
        borderColor: "border.default",
        borderRadius: 1,
        px: 1.25,
        py: 0.75,
        fontFamily: (t) => t.fonts.mono,
        fontSize: "0.75rem",
        wordBreak: "break-all",
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="flex-start"
        sx={{ width: "100%" }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {artifact.label && (
            <Typography
              variant="caption"
              sx={{
                display: "block",
                color: "text.secondary",
                fontSize: "0.7rem",
                mb: 0.25,
                fontFamily: "inherit",
              }}
            >
              {artifact.label}
            </Typography>
          )}
          {clickable ? (
            <a
              href={artifact.url}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "inherit" }}
            >
              {artifact.url}
            </a>
          ) : (
            <span>{artifact.url}</span>
          )}
        </Box>
        {isFetchable && (
          <Tooltip
            title={downloading ? "Downloading…" : "Download"}
            arrow
            placement="left"
          >
            <Box
              component="button"
              type="button"
              onClick={handleDownload}
              disabled={downloading}
              aria-label="Download artifact"
              sx={(t) => ({
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 28,
                height: 28,
                padding: 0,
                flexShrink: 0,
                borderRadius: 1,
                border: "1px solid",
                borderColor: "border.default",
                bgcolor: "surface.default",
                color: "text.secondary",
                cursor: downloading ? "wait" : "pointer",
                transition: `color ${t.motion.fast}, background-color ${t.motion.fast}, border-color ${t.motion.fast}`,
                "&:hover:not(:disabled)": {
                  color: "primary.main",
                  borderColor: "primary.main",
                  bgcolor: "accent.softer",
                },
                "&:disabled": { cursor: "wait" },
              })}
            >
              {downloading ? (
                <CircularProgress size={12} sx={{ color: "inherit" }} />
              ) : (
                <DownloadIcon sx={{ fontSize: 14 }} />
              )}
            </Box>
          </Tooltip>
        )}
      </Stack>
    </Box>
  );
}
